import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { termsService } from '@/api/services';
import { Button } from '@/components/common/Button/Button';
import naLogo from '@/assets/Nautic Alert Logo.png';

export function TermsAndConditionsPage() {
  const { accountUUID, setTermsAccepted, logout } = useAuth();
  const navigate = useNavigate();

  const [termsText, setTermsText] = useState('');
  const [loadError, setLoadError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');

  useEffect(() => {
    let cancelled = false;
    termsService.getTermsAndConditions()
      .then((text) => {
        if (!cancelled) setTermsText(text);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load Terms and Conditions. Please try again.');
      });
    return () => { cancelled = true; };
  }, []);

  const handleAccept = async () => {
    if (!accountUUID) return;
    setAccepting(true);
    setAcceptError('');
    try {
      await termsService.acceptTermsAndConditions(accountUUID);
      setTermsAccepted(true);
      navigate('/devices', { replace: true });
    } catch {
      setAcceptError('Failed to accept Terms and Conditions. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 dark:bg-navy-950">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="mb-6 text-center">
          <img src={naLogo} alt="Nautic Alert" className="mx-auto mb-3 h-14 object-contain" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Vessel Monitoring Portal</p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-lg dark:border-navy-700 dark:bg-navy-900">
          <div className="border-b border-slate-200 px-6 py-4 dark:border-navy-700">
            <h1 className="text-lg font-semibold text-slate-800 dark:text-white">
              Terms and Conditions
            </h1>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Please review and accept to continue.
            </p>
          </div>

          {/* Terms body */}
          <div className="h-96 overflow-y-auto px-6 py-4">
            {loadError ? (
              <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
            ) : termsText ? (
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {termsText}
              </pre>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 px-6 py-4 dark:border-navy-700">
            {acceptError && (
              <p className="mb-3 text-sm text-red-600 dark:text-red-400">{acceptError}</p>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={handleDecline} disabled={accepting}>
                Decline &amp; Sign Out
              </Button>
              <Button
                onClick={handleAccept}
                loading={accepting}
                disabled={!termsText || !!loadError}
              >
                Accept &amp; Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
